/**
 * AgeConsentForm — Corona Split Age Gate
 */
class AgeConsentForm {
  constructor(monthInput, dayInput, yearInput, consentCheckbox, submitBtn) {
    this.monthInput = document.querySelector(monthInput);
    this.dayInput = document.querySelector(dayInput);
    this.yearInput = document.querySelector(yearInput);
    this.consentCheckbox = document.querySelector(consentCheckbox);
    this.submitBtn = submitBtn ? document.querySelector(submitBtn) : null;
    this.ageGate = document.querySelector('#age-gate');

    this.init();
    this.setupFocusTrap();
    this._showGate();
  }

  init() {
    // On typing: just clear error messages (do NOT validate/focus)
    const clearErrors = () => this.validate({ show: false, focus: false });

    this.monthInput?.addEventListener('input', clearErrors);
    this.dayInput?.addEventListener('input', clearErrors);
    this.yearInput?.addEventListener('input', clearErrors);
    this.consentCheckbox?.addEventListener('change', clearErrors);

    // Clamp year value when user leaves the year field
    this.yearInput?.addEventListener('blur', () => this._clampYear());

    // Wire submit button
    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._handleSubmit();
      });
    }

    // Also support form submit event
    const form = this.monthInput?.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._handleSubmit();
      });
    }
  }

  setupFocusTrap() {
    // Tab-trap listener only — inert/aria-hidden is applied in _showGate()
    const focusableSelector =
      'input:not([disabled]), button:not([disabled]), a[href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    document.addEventListener('keydown', (e) => {
      if (!this.ageGate || this.ageGate.style.display === 'none') return;
      if (e.key !== 'Tab') return;

      const focusableElements = this.ageGate.querySelectorAll(focusableSelector);
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    });

    // Set initial focus to first input when age gate loads
    if (this.ageGate && this.monthInput) {
      setTimeout(() => this.monthInput.focus(), 150);
    }
  }

  // ---- Year clamping ----

  _clampYear() {
    const raw = this.yearInput?.value?.trim() ?? '';
    if (!raw) return; // don't clamp empty field

    const val = Number.parseInt(raw, 10);
    if (Number.isNaN(val)) return;

    const minYear = window.__ageGateMinYear ?? 1910;
    const maxYear = window.__ageGateMaxYear ?? new Date().getFullYear();

    if (val < minYear) {
      this.yearInput.value = minYear;
    } else if (val > maxYear) {
      this.yearInput.value = maxYear;
    }
  }

  // ---- Date helpers (from agegate-consent-check-modelo.js) ----

  isValidDate(year, month, day) {
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  is21OrOlder(year, month, day) {
    const dob = new Date(year, month - 1, day);
    const today = new Date();
    const age21 = new Date(today.getFullYear() - 21, today.getMonth(), today.getDate());
    return dob <= age21;
  }

  // ---- Validation (Modelo logic, plain .show CSS instead of Tailwind) ----

  validate({ show = true, focus = true } = {}) {
    const invalidDateMsg = document.getElementById('invalid-date-message');
    const invalidConsentMsg = document.getElementById('invalid-consent-message');

    const monthStr = this.monthInput?.value?.trim() ?? '';
    const dayStr = this.dayInput?.value?.trim() ?? '';
    const yearStr = this.yearInput?.value?.trim() ?? '';

    const month = Number.parseInt(monthStr, 10);
    const day = Number.parseInt(dayStr, 10);
    const year = Number.parseInt(yearStr, 10);

    const consentGiven = !!this.consentCheckbox?.checked;

    const missingParts =
      !monthStr || !dayStr || !yearStr ||
      Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year);

    const dateInvalid = !missingParts && !this.isValidDate(year, month, day);

    const hasDateError = missingParts || dateInvalid;
    const hasConsentError = !consentGiven;

    // If not showing (typing): force-hide both errors and reset aria-invalid
    if (!show) {
      invalidDateMsg?.classList.remove('show');
      if (invalidDateMsg) invalidDateMsg.textContent = '';
      invalidConsentMsg?.classList.remove('show');
      if (invalidConsentMsg) invalidConsentMsg.textContent = '';
      this.monthInput?.setAttribute('aria-invalid', 'false');
      this.dayInput?.setAttribute('aria-invalid', 'false');
      this.yearInput?.setAttribute('aria-invalid', 'false');
      this.consentCheckbox?.setAttribute('aria-invalid', 'false');
      return true;
    }

    // Show on submit — update aria-invalid states
    const monthInvalid = !monthStr || Number.isNaN(month);
    const dayInvalid = !dayStr || Number.isNaN(day);
    const yearInvalid = !yearStr || Number.isNaN(year);

    this.monthInput?.setAttribute('aria-invalid', String(monthInvalid || dateInvalid));
    this.dayInput?.setAttribute('aria-invalid', String(dayInvalid || dateInvalid));
    this.yearInput?.setAttribute('aria-invalid', String(yearInvalid || dateInvalid));
    this.consentCheckbox?.setAttribute('aria-invalid', String(hasConsentError));

    if (hasDateError) {
      if (invalidDateMsg) invalidDateMsg.textContent = invalidDateMsg.dataset.errorText ?? '';
      invalidDateMsg?.classList.add('show');
    } else {
      invalidDateMsg?.classList.remove('show');
      if (invalidDateMsg) invalidDateMsg.textContent = '';
    }

    // Populate consent error text dynamically so the live region only announces on error
    if (hasConsentError) {
      if (invalidConsentMsg) invalidConsentMsg.textContent = 'Please accept the privacy notice and website user agreement.';
      invalidConsentMsg?.classList.add('show');
    } else {
      invalidConsentMsg?.classList.remove('show');
      if (invalidConsentMsg) invalidConsentMsg.textContent = '';
    }

    if (focus && (hasDateError || hasConsentError)) {
      if (hasDateError) {
        if (!monthStr) return this.monthInput?.focus();
        if (!dayStr) return this.dayInput?.focus();
        if (!yearStr) return this.yearInput?.focus();
        return (dateInvalid ? this.dayInput : this.yearInput)?.focus();
      }
      if (hasConsentError) this.consentCheckbox?.focus();
    }

    return !(hasDateError || hasConsentError);
  }

  // ---- Submit handler ----

  _handleSubmit() {
    if (!this.validate({ show: true, focus: true })) return;

    const monthStr = this.monthInput?.value?.trim() ?? '';
    const dayStr = this.dayInput?.value?.trim() ?? '';
    const yearStr = this.yearInput?.value?.trim() ?? '';

    const month = Number.parseInt(monthStr, 10);
    const day = Number.parseInt(dayStr, 10);
    const year = Number.parseInt(yearStr, 10);

    if (this.is21OrOlder(year, month, day)) {
      this._setConsent();
    } else {
      const redirect = window.__ageGateRedirectUrl || '';
      setTimeout(() => {
        window.location.href =
          redirect && redirect.length
            ? redirect
            : 'https://www.responsibility.org/drink-responsibly/';
      }, 500);
    }
  }

  // ---- Cookie helpers ----

  _getCookie(name) {
    return (
      document.cookie
        .match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')
        ?.pop() || ''
    );
  }

  _setConsent() {
    const days = 15;
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = '; expires=' + date.toUTCString();

    const domain = window.__ageGateDomain || '';
    if (domain && window.location.href.includes(domain)) {
      document.cookie = 'validAge=true;' + expires + '; path=/; domain=' + domain;
    } else {
      document.cookie = 'validAge=true;' + expires + '; path=/;';
    }

    this._unlockPage();

    // Handle returnUrl query param
    const params = new URLSearchParams(window.location.search);
    let retURL = params.get('returnUrl');
    if (retURL) {
      if (!/^https?:\/\//i.test(retURL)) retURL = 'https://' + retURL;
      window.location.replace(retURL);
      return;
    }

    window.location.reload();
  }

  _lockPage() {
    const mainContent = document.querySelector('#MainContent');
    const header = document.querySelector('#shopify-section-header');
    const footer = document.querySelector('#shopify-section-footer');

    [mainContent, header, footer].forEach((el) => {
      if (!el) return;
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
    });
  }

  _unlockPage() {
    const mainContent = document.querySelector('#MainContent');
    const header = document.querySelector('#shopify-section-header');
    const footer = document.querySelector('#shopify-section-footer');

    [mainContent, header, footer].forEach((el) => {
      if (!el) return;
      el.removeAttribute('aria-hidden');
      el.removeAttribute('inert');
    });

    document.body.classList.remove('age-gate-active');
    document.body.classList.remove('lock-scroll');

    if (this.ageGate) {
      this.ageGate.style.display = 'none';
    }
  }

  // ---- Gate visibility on load ----

  _showGate() {
    if (!this.ageGate) return;

    // Never show on legal pages
    const path = window.location.pathname;
    if (path.includes('/pages/privacy') || path.includes('/pages/terms')) {
      const section = document.getElementById('shopify-section-age-gate');
      if (section) section.style.display = 'none';
      return;
    }

    const isOfAge = !!this._getCookie('validAge');

    if (isOfAge) {
      // Cookie present: keep gate hidden, ensure page is fully unlocked
      this.ageGate.style.display = 'none';
      document.body.classList.remove('age-gate-active');
      document.body.classList.remove('lock-scroll');
      this._unlockPage();
    } else {
      // No cookie: show gate, lock body and hide background from assistive tech
      this.ageGate.style.display = '';
      document.body.classList.add('age-gate-active');
      this._lockPage();
    }
  }
}